/**
 * Bulk per-patient hydration for the CI queues.
 *
 * The queues used to pull everything through nested GraphQL reverse-chains: one
 * sub-query per patient per resource type, so cost scaled as rows x types. On
 * post-consultation that was EIGHT sub-queries a row, and at 100 rows the single
 * request consumed 64k of Medplum's 50k-point budget and came back
 * "Too Many Requests" with a null list — which the tab renders as an empty
 * queue, with nothing on screen naming the cause.
 *
 * Here the root query returns appointments only, and everything hanging off the
 * patient is fetched in a handful of BULK searches scoped by
 * `patient=id1,id2,...`. Cost becomes roughly one request per resource type,
 * independent of row count.
 *
 * `lastCommunication` is bulked too, and paginated to completion
 * (`searchAllByPatients`) so it stays exact: a single `_count` window across a
 * chunk would crowd a quiet patient's last contact out and read them as
 * never-contacted, which the "No contact 7 days" chip cannot afford. It used to
 * stay a nested `_count: 1` chain for that reason, but "one cheap sub-query per
 * row" measured ~20 points a row on production (2026-09-28): with the queues
 * loading their whole cohort, the 1,152-patient All inquiry cohort spent ~38k of
 * a user's 50k-point minute on the root query alone, and reps hit "Too Many
 * Requests" on every other load. The search is bounded by a `sent` floor
 * instead — see `LAST_COMMUNICATION_LOOKBACK_DAYS`.
 */

import dayjs from 'dayjs';
import { MedplumClient } from '@medplum/core';
import { Resource } from '@medplum/fhirtypes';
import { ACTIVE_LEAD_INACTIVE_DAYS } from '@bariendo/lib/active-lead';

/**
 * Ids per search. A comma-joined `patient` parameter goes in the query string,
 * and an unbounded list eventually exceeds what the server will accept — the
 * failure mode the stage-tag prefetch this replaced used to hit. Chunking keeps
 * each URL short regardless of page size.
 *
 * 150, not 50, because the queues now load their whole date-scoped cohort rather
 * than a page: cost is `ceil(rows / CHUNK) x resource types`, so the divisor is
 * what keeps a 2,000-row cohort at a dozen searches per type instead of forty.
 * A chunk of 150 UUID references is ~7 KB of query string, comfortably inside
 * the ~8 KB a server will take.
 */
export const HYDRATION_CHUNK_SIZE = 150;

/**
 * How far back the Last communication read looks, for the queues whose rows
 * can be older than their scope (pre- and post-consultation).
 *
 * Exactly `ACTIVE_LEAD_INACTIVE_DAYS`, and no shorter: a contact older than that
 * can no longer make a lead active (nor clear the 7-day No contact chip), so
 * dropping it changes neither — only the date shown, which reads as no contact
 * instead. A shorter floor would drop a contact that is still keeping a lead
 * active and flip it to Inactive off an older appointment.
 */
export const LAST_COMMUNICATION_LOOKBACK_DAYS = ACTIVE_LEAD_INACTIVE_DAYS;

/** The `sent` floor for a queue whose rows can be older than its scope. */
export function lastCommunicationFloor(today: Date): string {
  return dayjs(today).subtract(LAST_COMMUNICATION_LOOKBACK_DAYS, 'day').format('YYYY-MM-DD');
}

export { LAST_COMMUNICATION_CATEGORIES } from '@bariendo/lib/active-lead';

export function chunk<T>(items: T[], size = HYDRATION_CHUNK_SIZE): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

/**
 * One bulk search per chunk of patients, flattened.
 *
 * `searchParam` is the resource's own link to the patient — `patient` for
 * Appointment/Task/EpisodeOfCare, `subject` for Flag/QuestionnaireResponse.
 * Getting it wrong returns an empty list rather than an error, so the callers
 * below name it explicitly at every call site.
 */
export async function searchByPatients<T extends Resource>(
  medplum: MedplumClient,
  resourceType: T['resourceType'],
  searchParam: 'patient' | 'subject',
  patientRefs: string[],
  params: Record<string, string>
): Promise<T[]> {
  if (patientRefs.length === 0) {
    return [];
  }

  const pages = await Promise.all(
    chunk(patientRefs).map((refs) =>
      medplum.searchResources(resourceType, {
        ...params,
        [searchParam]: refs.join(','),
      } as Record<string, string>)
    )
  );

  return pages.flat() as T[];
}

/**
 * `searchByPatients`, but following `next` links until the result is complete.
 *
 * For searches whose answer must not be truncated — a "latest per patient" read
 * is only exact if every match comes back, since one `_count` window across a
 * whole chunk can crowd a quiet patient out. Each page is one ~20-point request,
 * so bound the search (a `sent` floor, say) rather than relying on the pages.
 */
export async function searchAllByPatients<T extends Resource>(
  medplum: MedplumClient,
  resourceType: T['resourceType'],
  searchParam: 'patient' | 'subject',
  patientRefs: string[],
  params: Record<string, string>
): Promise<T[]> {
  if (patientRefs.length === 0) {
    return [];
  }

  const chunks = await Promise.all(
    chunk(patientRefs).map(async (refs) => {
      const found: T[] = [];
      const query = { ...params, [searchParam]: refs.join(',') } as Record<string, string>;
      for await (const page of medplum.searchResourcePages(resourceType, query)) {
        found.push(...(page as unknown as T[]));
      }
      return found;
    })
  );

  return chunks.flat();
}

/** Groups resources under the patient reference found by `keyOf`. */
export function groupByPatient<T>(resources: T[], keyOf: (resource: T) => string | undefined): Map<string, T[]> {
  const grouped = new Map<string, T[]>();

  for (const resource of resources) {
    const key = keyOf(resource);
    if (!key) {
      continue;
    }
    const existing = grouped.get(key);
    if (existing) {
      existing.push(resource);
    } else {
      grouped.set(key, [resource]);
    }
  }

  return grouped;
}

/**
 * First resource per patient, preserving the order the server returned.
 *
 * Callers sort server-side (`_sort`) and rely on that order, so "first" means
 * "newest" or "earliest" depending on the search — never re-sorted here, because
 * re-deriving the order client-side is how the two drift.
 */
export function firstByPatient<T>(resources: T[], keyOf: (resource: T) => string | undefined): Map<string, T> {
  const first = new Map<string, T>();

  for (const resource of resources) {
    const key = keyOf(resource);
    if (key && !first.has(key)) {
      first.set(key, resource);
    }
  }

  return first;
}

/** References a page introduces that no earlier page has hydrated. */
export function newRefsOnly(refs: string[], held: Set<string>): string[] {
  return refs.filter((reference) => !held.has(reference));
}

/**
 * One hydration map folded onto the accumulated one, later entries winning.
 *
 * The queues hydrate incrementally — each page fetches only its own new patients
 * — so a page's result covers only that page. Replacing the map instead of
 * merging it would blank every derived column on the pages already on screen.
 */
export function mergeMap<T>(previous: Map<string, T>, next: Map<string, T>): Map<string, T> {
  if (previous.size === 0) {
    return next;
  }

  const merged = new Map(previous);

  for (const [key, value] of next) {
    merged.set(key, value);
  }

  return merged;
}
